import React from 'react';
import { Box, Container, Grid, Card, CardContent, Typography } from '@mui/material';
import {
  CalendarToday,
  People,
  Assessment,
  Security,
  Sync,
  Notifications,
} from '@mui/icons-material';

const features = [
  {
    icon: CalendarToday,
    title: 'Smart Scheduling',
    description: 'AI-powered shift scheduling that optimizes coverage while respecting employee preferences and compliance rules.',
    color: '#E91E63',
    gradient: 'linear-gradient(135deg, #E91E63 0%, #F48FB1 100%)',
  },
  {
    icon: People,
    title: 'Staff Management',
    description: 'Complete employee profiles, skills tracking, and availability management in one centralized platform.',
    color: '#9C27B0',
    gradient: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
  },
  {
    icon: Assessment,
    title: 'Analytics & Reports',
    description: 'Real-time insights into attendance, overtime, and labor costs with beautiful visualizations.',
    color: '#FF9800',
    gradient: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
  },
  {
    icon: Security,
    title: 'Compliance Ready',
    description: 'Stay compliant with labor laws and industry regulations with automated tracking and alerts.',
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
  },
  {
    icon: Sync,
    title: 'Real-time Sync',
    description: 'Changes sync instantly across all devices. Everyone stays up-to-date automatically.',
    color: '#2196F3',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)',
  },
  {
    icon: Notifications,
    title: 'Smart Notifications',
    description: 'Timely alerts for shift changes, approvals, and important updates via email, SMS, or push.',
    color: '#E91E63',
    gradient: 'linear-gradient(135deg, #E91E63 0%, #F48FB1 100%)',
  },
];

const FeatureCards: React.FC = () => {
  return (
    <Box
      sx={{
        py: 12,
        background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '-10%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(233, 30, 99, 0.08) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          right: '-10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(156, 39, 176, 0.08) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: 'white',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Everything You{' '}
            <span className="gradient-text">Need</span>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Powerful features designed for modern healthcare organizations to streamline operations and empower teams
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                className="glass-card glass-card-hover"
                sx={{
                  height: '100%',
                  bgcolor: 'rgba(26, 26, 46, 0.6)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: `fadeIn 0.6s ease-out ${index * 0.1}s forwards`,
                  opacity: 0,
                  transform: 'translateY(20px)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* Icon with gradient background */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: feature.gradient,
                      mb: 3,
                      boxShadow: `0 8px 30px ${feature.color}40`,
                      transition: 'all 0.3s ease',
                      '& svg': {
                        color: 'white',
                        fontSize: 32,
                      },
                      '&:hover': {
                        transform: 'scale(1.1) rotate(5deg)',
                        boxShadow: `0 15px 50px ${feature.color}60`,
                      },
                    }}
                  >
                    <feature.icon />
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      mb: 2,
                      fontSize: '1.25rem',
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      lineHeight: 1.7,
                    }}
                  >
                    {feature.description}
                  </Typography>

                  {/* Learn more link */}
                  <Box
                    sx={{
                      mt: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: feature.color,
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'gap 0.3s ease',
                      '&:hover': {
                        gap: 1.5,
                      },
                    }}
                  >
                    Learn more
                    <span>→</span>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Box
          sx={{
            mt: 10,
            p: 6,
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.1) 0%, rgba(156, 39, 176, 0.1) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
            Ready to transform your scheduling?
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 4 }}>
            Join thousands of healthcare organizations using Sakura ShiftSync
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4CAF50' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                No credit card required
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4CAF50' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                14-day free trial
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4CAF50' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Cancel anytime
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default FeatureCards;
